import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Scissors } from 'lucide-react';
import nurseryMaterialService from '../../services/nurseryMaterial';
import nurseryPropagationService from '../../services/nurseryPropagation';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import type { LicensedCultivar, NurseryLocation } from '../../types/nursery';
import type { PropagationFamily, PropagationMethod } from '../../types/nurseryPropagation';
import { MATERIAL_KINDS, MATERIAL_KIND_LABELS, UNITS_FOR_KIND } from '../../types/nurseryMaterial';
import type { MaterialKind, MaterialPayload, MaterialSources, MaterialUnit } from '../../types/nurseryMaterial';

/**
 * /nursery/material/new — record a bundle of material that was cut, collected
 * or bought in (propagation plan, Phase 1).
 *
 * Arrives prefilled from a mother (?source_lot=, ?source_area=&cultivar_id=)
 * or a sourcing request (?request=&kind=&cultivar_id=&source_text=), which the
 * save closes.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

/** The kind of method a bundle of each kind is used by, to offer its wood window. */
const FAMILY_FOR_KIND: Record<MaterialKind, PropagationFamily> = {
  seed: 'seed', cutting: 'cutting', budwood: 'bud', graftwood: 'graft', explant: 'tissue_culture',
};

const isKind = (v: string | null): v is MaterialKind => !!v && (MATERIAL_KINDS as string[]).includes(v);

function MaterialForm() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [params] = useSearchParams();
  const requestId = Number(params.get('request')) || null;
  const presetLot = Number(params.get('source_lot')) || null;
  const presetArea = Number(params.get('source_area')) || null;

  const [kind, setKind] = useState<MaterialKind>(isKind(params.get('kind')) ? params.get('kind') as MaterialKind : 'budwood');
  const [cultivarId, setCultivarId] = useState<number | ''>(Number(params.get('cultivar_id')) || '');
  const [unit, setUnit] = useState<MaterialUnit>(UNITS_FOR_KIND[kind][0]);
  const [quantity, setQuantity] = useState('');
  const [isEstimate, setIsEstimate] = useState(true);
  // 'lot:<id>', 'area:<id>', 'supplier' or 'other'.
  const [from, setFrom] = useState<string>(presetLot ? `lot:${presetLot}` : presetArea ? `area:${presetArea}` : params.get('source_text') ? 'other' : '');
  const [sourceText, setSourceText] = useState(params.get('source_text') ?? '');
  const [collectedOn, setCollectedOn] = useState(getTodayInTz(orgTz));
  const [methodId, setMethodId] = useState<number | ''>('');
  const [useBy, setUseBy] = useState('');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [seedsPerKg, setSeedsPerKg] = useState('');
  const [stratifyStart, setStratifyStart] = useState('');
  const [certScheme, setCertScheme] = useState('');
  const [certNumber, setCertNumber] = useState('');
  const [notes, setNotes] = useState('');

  const [cultivars, setCultivars] = useState<LicensedCultivar[]>([]);
  const [sources, setSources] = useState<MaterialSources | null>(null);
  const [methods, setMethods] = useState<PropagationMethod[]>([]);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getLicensedCultivars().then((r) => { if (r.success) setCultivars(r.data || []); }).catch(() => { /* typed by id below */ });
    nurseryPropagationService.getMethods().then((r) => { if (r.success) setMethods(r.data || []); }).catch(() => { /* no wood windows */ });
    nurseryService.getLocations({ active: true }).then((r) => { if (r.success) setLocations(r.data || []); }).catch(() => { /* not stored anywhere */ });
  }, []);

  // A mother lot names its own cultivar: take it, so the form opens complete.
  useEffect(() => {
    if (!presetLot || cultivarId !== '') return;
    nurseryService.getLot(presetLot).then((r) => { if (r.success && r.data) setCultivarId(r.data.cultivar_id); }).catch(() => { /* chosen by hand */ });
  }, [presetLot, cultivarId]);

  useEffect(() => {
    if (cultivarId === '') { setSources(null); return; }
    nurseryMaterialService.sources(Number(cultivarId))
      .then((r) => setSources(r.success ? r.data : null))
      .catch(() => setSources(null));
  }, [cultivarId]);

  const kindMethods = useMemo(() => methods.filter((m) => m.family === FAMILY_FOR_KIND[kind]), [methods, kind]);
  const method = kindMethods.find((m) => m.id === methodId);
  const suggestedUseBy = method?.wood_window_days != null && collectedOn ? addDays(collectedOn, method.wood_window_days) : null;
  const storePlaces = locations.filter((l) => l.location_type !== 'nursery_row');
  const groupOf = (key: string) => sources?.groups.find((g) => g.key === key)?.rows ?? [];

  const chooseKind = (k: MaterialKind) => {
    setKind(k);
    setUnit(UNITS_FOR_KIND[k][0]);
    setMethodId('');
  };

  const save = async () => {
    if (cultivarId === '') { setError('Choose the cultivar'); return; }
    const q = Number(quantity);
    if (!(q > 0)) { setError('Enter how much'); return; }
    if (!from) { setError('Say where it came from'); return; }
    if ((from === 'supplier' || from === 'other') && !sourceText.trim()) { setError('Say who or where it came from'); return; }
    const payload: MaterialPayload = {
      kind, cultivar_id: Number(cultivarId), unit, quantity: q, is_estimate: isEstimate,
      collected_on: collectedOn,
      source_type: from.startsWith('lot:') ? 'lot' : from.startsWith('area:') ? 'cultivar_area' : from as 'supplier' | 'other',
      ...(from.startsWith('lot:') ? { source_lot_id: Number(from.slice(4)) } : {}),
      ...(from.startsWith('area:') ? { source_cultivar_area_id: Number(from.slice(5)) } : {}),
      source_text: sourceText.trim() || null,
      use_by: useBy || null,
      propagation_method_id: methodId === '' ? null : Number(methodId),
      location_id: locationId === '' ? null : Number(locationId),
      certificate_scheme: certScheme.trim() || null,
      certificate_number: certNumber.trim() || null,
      notes: notes.trim() || null,
      ...(kind === 'seed' ? {
        seeds_per_kg: seedsPerKg.trim() === '' ? null : Number(seedsPerKg),
        stratify_start: stratifyStart || null,
      } : {}),
      ...(requestId ? { sourcing_request_id: requestId } : {}),
    };
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryMaterialService.create(payload);
      if (!res.success) { setError(errorMessage(res, 'Could not save the material')); return; }
      navigate(`/nursery/material/${res.data.id}`);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the material'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Add material" subtitle={requestId ? 'Collected for a request' : 'Cut, collected or bought in'}
        icon={<Scissors className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mat-kind" className="block text-sm font-medium text-neutral-700 mb-1">What</label>
              <select id="mat-kind" className={inputCls} value={kind} disabled={!!requestId}
                onChange={(e) => chooseKind(e.target.value as MaterialKind)}>
                {MATERIAL_KINDS.map((k) => <option key={k} value={k}>{MATERIAL_KIND_LABELS[k]}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="mat-cultivar" className="block text-sm font-medium text-neutral-700 mb-1">Of</label>
              <select id="mat-cultivar" className={inputCls} value={cultivarId} disabled={!!requestId}
                onChange={(e) => { setCultivarId(e.target.value ? Number(e.target.value) : ''); setFrom(''); }}>
                <option value="">Choose a cultivar…</option>
                {cultivars.map((c) => <option key={c.id} value={c.id}>{c.cultivar_name}</option>)}
                {cultivarId !== '' && !cultivars.some((c) => c.id === cultivarId) && (
                  <option value={cultivarId}>{`Cultivar #${cultivarId}`}</option>
                )}
              </select>
            </div>
            <div>
              <label htmlFor="mat-qty" className="block text-sm font-medium text-neutral-700 mb-1">How much</label>
              <input id="mat-qty" className={inputCls} type="text" inputMode="decimal" value={quantity}
                onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div>
              <label htmlFor="mat-unit" className="block text-sm font-medium text-neutral-700 mb-1">Counted in</label>
              <select id="mat-unit" className={inputCls} value={unit} onChange={(e) => setUnit(e.target.value as MaterialUnit)}>
                {UNITS_FOR_KIND[kind].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <label className="sm:col-span-2 inline-flex items-start gap-2 text-sm text-neutral-700">
              <input type="checkbox" checked={isEstimate} onChange={(e) => setIsEstimate(e.target.checked)} className="mt-0.5 rounded border-neutral-300" />
              <span>
                It is an estimate
                <span className="block text-xs text-neutral-500">
                  {isEstimate
                    ? 'Drawing more than this from it is allowed, and recorded as an overrun.'
                    : 'Counted exactly: more than this cannot be drawn from it.'}
                </span>
              </span>
            </label>
          </div>

          <div>
            <label htmlFor="mat-from" className="block text-sm font-medium text-neutral-700 mb-1">Came from</label>
            <select id="mat-from" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)}>
              <option value="">Choose…</option>
              {groupOf('mothers').length > 0 && (
                <optgroup label="Mother plants">
                  {groupOf('mothers').map((r) => (
                    <option key={`m${r.id}`} value={`lot:${r.id}`}>
                      {[r.lot_code || `Lot #${r.id}`, r.location_name].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </optgroup>
              )}
              {groupOf('lots').length > 0 && (
                <optgroup label="Nursery lots">
                  {groupOf('lots').map((r) => (
                    <option key={`l${r.id}`} value={`lot:${r.id}`}>
                      {[r.lot_code || `Lot #${r.id}`, r.tree_type_name, r.location_name].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </optgroup>
              )}
              {groupOf('plantings').length > 0 && (
                <optgroup label="Orchard plantings">
                  {groupOf('plantings').map((r) => (
                    <option key={`a${r.id}`} value={`area:${r.id}`}>
                      {[r.farm_name, r.name, r.is_wood_source ? 'wood source' : null].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </optgroup>
              )}
              {from.startsWith('lot:') && !sources?.groups.some((g) => g.rows.some((r) => `lot:${r.id}` === from)) && (
                <option value={from}>{`Lot #${from.slice(4)}`}</option>
              )}
              {from.startsWith('area:') && !groupOf('plantings').some((r) => `area:${r.id}` === from) && (
                <option value={from}>{`Planting #${from.slice(5)}`}</option>
              )}
              <option value="supplier">A supplier</option>
              <option value="other">Somewhere else</option>
            </select>
            {cultivarId !== '' && sources && !sources.first && (
              <p className="text-xs text-neutral-500 mt-1">
                No mother plants, nursery lots or orchard plantings of this cultivar. Say who or where it came from.
              </p>
            )}
          </div>
          {(from === 'supplier' || from === 'other') && (
            <div>
              <label htmlFor="mat-source-text" className="block text-sm font-medium text-neutral-700 mb-1">
                {from === 'supplier' ? 'Supplier' : 'Where'}
              </label>
              <input id="mat-source-text" className={inputCls} maxLength={255} value={sourceText}
                onChange={(e) => setSourceText(e.target.value)} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mat-collected" className="block text-sm font-medium text-neutral-700 mb-1">Collected on</label>
              <input id="mat-collected" className={inputCls} type="date" value={collectedOn} onChange={(e) => setCollectedOn(e.target.value)} />
            </div>
            {kindMethods.length > 0 && (
              <div>
                <label htmlFor="mat-method" className="block text-sm font-medium text-neutral-700 mb-1">For</label>
                <select id="mat-method" className={inputCls} value={methodId}
                  onChange={(e) => setMethodId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">No method</option>
                  {kindMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="mat-use-by" className="block text-sm font-medium text-neutral-700 mb-1">Use by</label>
              <input id="mat-use-by" className={inputCls} type="date" value={useBy} onChange={(e) => setUseBy(e.target.value)} />
              {!useBy && suggestedUseBy && (
                <p className="text-xs text-neutral-500 mt-1">{`Left blank, ${formatLocalDateInTz(suggestedUseBy, orgTz)}: the method's wood window.`}</p>
              )}
            </div>
            {storePlaces.length > 0 && (
              <div>
                <label htmlFor="mat-location" className="block text-sm font-medium text-neutral-700 mb-1">Kept at</label>
                <select id="mat-location" className={inputCls} value={locationId}
                  onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Not recorded</option>
                  {storePlaces.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {kind === 'seed' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="mat-seeds-kg" className="block text-sm font-medium text-neutral-700 mb-1">Seeds per kg</label>
                <input id="mat-seeds-kg" className={inputCls} type="text" inputMode="decimal" value={seedsPerKg}
                  onChange={(e) => setSeedsPerKg(e.target.value)} />
              </div>
              <div>
                <label htmlFor="mat-stratify" className="block text-sm font-medium text-neutral-700 mb-1">Stratification started</label>
                <input id="mat-stratify" className={inputCls} type="date" value={stratifyStart} onChange={(e) => setStratifyStart(e.target.value)} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mat-cert-scheme" className="block text-sm font-medium text-neutral-700 mb-1">Certificate scheme</label>
              <input id="mat-cert-scheme" className={inputCls} maxLength={100} value={certScheme}
                placeholder="e.g. high-health, virus-tested" onChange={(e) => setCertScheme(e.target.value)} />
            </div>
            <div>
              <label htmlFor="mat-cert-number" className="block text-sm font-medium text-neutral-700 mb-1">Certificate number</label>
              <input id="mat-cert-number" className={inputCls} maxLength={100} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
            </div>
          </div>
          <div>
            <label htmlFor="mat-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea id="mat-notes" className={inputCls} rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save material'}</Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default MaterialForm;
